import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Tooltip
} from '@mui/material';
import { Edit, Refresh, Check, Close } from '@mui/icons-material';
import api from '../../../services/api/client';

export default function PlansManagement({ onUpdate }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/credits/plans?include_inactive=true');
      setPlans(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleEdit = (plan) => {
    setEditingPlan({ ...plan });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingPlan) return;

    try {
      setSaving(true);
      await api.patch(`/admin/credits/plans/${editingPlan.id}`, {
        name: editingPlan.name,
        monthly_price_eur: parseFloat(editingPlan.monthly_price_eur),
        monthly_credits: parseInt(editingPlan.monthly_credits),
        max_workspaces: parseInt(editingPlan.max_workspaces),
        is_active: editingPlan.is_active,
        display_order: parseInt(editingPlan.display_order) || 0
      });
      setEditDialog(false);
      setEditingPlan(null);
      fetchPlans();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Gestion des Plans ({plans.length})
        </Typography>
        <Button startIcon={<Refresh />} onClick={fetchPlans}>
          Actualiser
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Nom</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell align="right">Prix/mois (EUR)</TableCell>
              <TableCell align="right">Credits/mois</TableCell>
              <TableCell align="right">Max Workspaces</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="center">Ordre</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id} hover>
                <TableCell>
                  <Typography fontWeight="medium">{plan.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={plan.slug} size="small" />
                </TableCell>
                <TableCell align="right">
                  {plan.monthly_price_eur?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell align="right">
                  {plan.monthly_credits?.toLocaleString() || 0}
                </TableCell>
                <TableCell align="right">
                  {plan.max_workspaces === -1 ? 'Illimite' : plan.max_workspaces}
                </TableCell>
                <TableCell align="center">
                  {plan.is_active ? (
                    <Chip label="Actif" color="success" size="small" />
                  ) : (
                    <Chip label="Inactif" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">{plan.display_order || 0}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => handleEdit(plan)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le Plan</DialogTitle>
        <DialogContent>
          {editingPlan && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nom"
                value={editingPlan.name || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Prix mensuel (EUR)"
                type="number"
                value={editingPlan.monthly_price_eur || 0}
                onChange={(e) => setEditingPlan({ ...editingPlan, monthly_price_eur: e.target.value })}
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
              />
              <TextField
                label="Credits mensuels"
                type="number"
                value={editingPlan.monthly_credits || 0}
                onChange={(e) => setEditingPlan({ ...editingPlan, monthly_credits: e.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max Workspaces (-1 = illimite)"
                type="number"
                value={editingPlan.max_workspaces || 0}
                onChange={(e) => setEditingPlan({ ...editingPlan, max_workspaces: e.target.value })}
                fullWidth
                inputProps={{ min: -1 }}
              />
              <TextField
                label="Ordre d'affichage"
                type="number"
                value={editingPlan.display_order || 0}
                onChange={(e) => setEditingPlan({ ...editingPlan, display_order: e.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingPlan.is_active || false}
                    onChange={(e) => setEditingPlan({ ...editingPlan, is_active: e.target.checked })}
                  />
                }
                label="Plan actif"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Check />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
